import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { getIdToken, NO_CURRENT_USER } from "@/utils/authSession";
import { Loader2, ShieldCheck, AlertCircle, User, Building2, Link2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import logoCYT from "/images/logo-black.png";

const SCOPE_DESCRIPTIONS = {
  profile: {
    icon: User,
    label: "Ver tu información de perfil",
    description: "Nombre, correo electrónico",
  },
  client: {
    icon: Building2,
    label: "Ver información de tu organización",
    description: "Nombre de la empresa, logo, tipo de cuenta",
  },
};

async function getAuthHeaders() {
  return {
    Authorization: `Bearer ${await getIdToken()}`,
    "Content-Type": "application/json",
  };
}

/**
 * Message to show for a failed request. getIdToken rejects with a plain string when the session
 * ended, which has no `message`.
 */
function getErrorMessage(err) {
  if (err === NO_CURRENT_USER) return "Tu sesión terminó. Inicia sesión de nuevo.";
  return err?.message || "Ocurrió un error, intenta de nuevo más tarde";
}

/**
 * Safely parse a JSON response body, returning null if parsing fails
 * (e.g. if the server returns a non-JSON error like a 502 gateway error).
 */
async function safeParseJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export default function OAuthAuthorize() {
  const [searchParams] = useSearchParams();
  const buildTimeApiUrl = import.meta.env.VITE_EXTERNAL_API_URL || "http://localhost:8000";
  const EXTERNAL_API_URL = searchParams.get("cyt_api_url") || buildTimeApiUrl;
  const [appInfo, setAppInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorizing, setAuthorizing] = useState(false);

  const clientId = searchParams.get("client_id");
  const redirectUri = searchParams.get("redirect_uri");
  const responseType = searchParams.get("response_type") || "code";
  const scope = searchParams.get("scope") || "profile client";
  const state = searchParams.get("state");

  useEffect(() => {
    async function validateRequest() {
      if (!clientId || !redirectUri) {
        setError("Faltan parámetros requeridos (client_id, redirect_uri)");
        setLoading(false);
        return;
      }

      try {
        const headers = await getAuthHeaders();
        const params = new URLSearchParams({
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: responseType,
          scope: scope,
          ...(state && { state }),
        });

        const response = await fetch(`${EXTERNAL_API_URL}/api/oauth/authorize/?${params}`, {
          headers,
        });

        if (!response.ok) {
          const data = await safeParseJson(response);
          throw new Error(data?.error || data?.detail || `Error del servidor (${response.status})`);
        }

        const data = await response.json();
        setAppInfo(data);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    }

    validateRequest();
  }, [clientId, redirectUri, responseType, scope, state, EXTERNAL_API_URL]);

  const handleAuthorize = async () => {
    setAuthorizing(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${EXTERNAL_API_URL}/api/oauth/authorize/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          client_id: clientId,
          redirect_uri: redirectUri,
          response_type: responseType,
          scope: scope,
          state: state,
        }),
      });

      if (!response.ok) {
        const data = await safeParseJson(response);
        throw new Error(data?.error || data?.detail || `Error del servidor (${response.status})`);
      }

      const data = await response.json();

      // Use the server-validated redirect_uri from the response (not from query params)
      // to prevent TOCTOU redirect manipulation attacks.
      const serverRedirectUri = data.redirect_uri;
      if (!serverRedirectUri) {
        throw new Error("El servidor no devolvió una redirect_uri válida");
      }
      const redirectUrl = new URL(serverRedirectUri);
      redirectUrl.searchParams.set("code", data.code);

      // Use server-echoed state, falling back to original state param
      const stateToReturn = data.state || state;
      if (stateToReturn) {
        redirectUrl.searchParams.set("state", stateToReturn);
      }

      window.location.href = redirectUrl.toString();
    } catch (err) {
      setError(getErrorMessage(err));
      setAuthorizing(false);
    }
  };

  const handleDeny = () => {
    // Use the server-validated redirect_uri from appInfo (not from raw query params)
    // to prevent open redirect attacks on the deny flow.
    const validatedRedirectUri = appInfo?.redirect_uri;
    if (!validatedRedirectUri) return;

    const redirectUrl = new URL(validatedRedirectUri);
    redirectUrl.searchParams.set("error", "access_denied");
    if (state) {
      redirectUrl.searchParams.set("state", state);
    }
    window.location.href = redirectUrl.toString();
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-gray-400" />
          <p className="mt-2 text-sm text-gray-500">Validando solicitud...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="text-xl">Error de autorización</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const scopes = appInfo?.scopes || [];

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <img src={logoCYT} alt="Casas y Terrenos" className="h-10" />
          </div>

          {appInfo?.application_logo_url && (
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border bg-white">
              <img
                src={appInfo.application_logo_url}
                alt={appInfo.application_name}
                className="h-12 w-12 object-contain"
              />
            </div>
          )}

          <CardTitle className="text-xl">
            {appInfo?.application_name} quiere acceder a tu cuenta
          </CardTitle>
          <CardDescription>
            {appInfo?.application_description || (
              <>Esta aplicación solicita acceso a la siguiente información:</>
            )}
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="space-y-3">
            {scopes.map((scopeKey) => {
              const scopeInfo = SCOPE_DESCRIPTIONS[scopeKey];
              if (!scopeInfo) return null;
              const Icon = scopeInfo.icon;
              return (
                <div key={scopeKey} className="flex items-start gap-3 rounded-lg border p-3">
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50">
                    <Icon className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{scopeInfo.label}</p>
                    <p className="text-xs text-gray-500">{scopeInfo.description}</p>
                  </div>
                </div>
              );
            })}

            <div className="flex items-start gap-3 rounded-lg border p-3">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50">
                <Link2 className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium">
                  Ligar tu cuenta de Casas y Terrenos con {appInfo?.application_name}
                </p>
                <p className="text-xs text-gray-500">
                  Tu organización quedará vinculada a este CRM para sincronizar tu información
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 p-3">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs text-amber-700">
              Solo los administradores de organización pueden autorizar acceso a aplicaciones de
              terceros.
            </p>
          </div>

          {appInfo?.crm_replace_warning && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-orange-300 bg-orange-50 p-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
              <p className="text-xs text-orange-800">
                Ya tienes una integración con{" "}
                <span className="font-semibold">
                  {appInfo.crm_replace_warning.current_feed_name}
                </span>
                . Al autorizar, se reemplazará con{" "}
                <span className="font-semibold">{appInfo.application_name}</span>.
              </p>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={handleDeny} disabled={authorizing}>
            Denegar
          </Button>
          <Button className="flex-1" onClick={handleAuthorize} disabled={authorizing}>
            {authorizing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Autorizar
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
